'use client'

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Worker, WorkerListItem } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  DataTable,
  TextField,
  Segmented,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  WalletIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced, useFormatters } from '@/lib/hooks.js'
import { WorkerFormDialog } from '@/components/WorkerFormDialog.js'
import { DepartmentsView } from '@/components/DepartmentsView.js'

type StaffView = 'workers' | 'departments'

export function StaffPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  const [searchParams, setSearchParams] = useSearchParams()
  const view = (searchParams.get('view') === 'departments' ? 'departments' : 'workers') as StaffView
  const setView = (next: StaffView) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'workers') params.delete('view')
    else params.set('view', next)
    setSearchParams(params)
  }

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounced(searchInput, 250)

  const [deleteWorkerId, setDeleteWorkerId] = useState<string | null>(null)
  const [editingWorker, setEditingWorker] = useState<Worker | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.workers({ q: debouncedSearch }),
    queryFn: () => api.workers.list({ q: debouncedSearch }),
    enabled: view === 'workers',
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.workers.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(t('staff.workerRemoved'))
      setDeleteWorkerId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('staff.failedToRemove'))
    },
  })

  const handleEdit = (worker: Worker) => {
    setEditingWorker(worker)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingWorker(undefined)
  }

  const handleAddNew = () => {
    setEditingWorker(undefined)
    setFormDialogOpen(true)
  }

  const items = data?.items ?? []
  const hasSearchQuery = debouncedSearch.trim() !== ''
  const isEmpty = items.length === 0

  /** Positive: the worker owes the company. Negative: the company owes them. Zero: settled. */
  const renderBalance = (balanceMinor: number) => {
    if (balanceMinor === 0) {
      return <span className="text-on-surface-variant">{t('staff.settledUp')}</span>
    }
    if (balanceMinor > 0) {
      return <span className="text-error font-medium">{t('staff.owes', { amount: formatters.money(balanceMinor) })}</span>
    }
    return <span className="text-primary font-medium">{t('staff.youOwe', { amount: formatters.money(-balanceMinor) })}</span>
  }

  const columns: Column<WorkerListItem>[] = [
    {
      key: 'name',
      header: t('staff.columns.name'),
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: 'phone',
      header: t('staff.columns.phone'),
      cell: (row) => <span className="ref">{row.phone || '—'}</span>,
      hideOnMobile: true,
      width: 'w-40',
    },
    {
      key: 'department',
      header: t('staff.columns.department'),
      cell: (row) => row.departmentName || <span className="text-on-surface-variant">{t('departmentForm.noDepartment')}</span>,
      hideOnMobile: true,
      width: 'w-40',
    },
    {
      key: 'balance',
      header: t('staff.columns.balance'),
      cell: (row) => renderBalance(row.balanceMinor),
      numeric: true,
      width: 'w-48',
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('staff.editWorker')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('staff.removeWorker')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteWorkerId(row.id)
            }}
          />
        </div>
      ),
      width: 'w-24',
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('staff.title')}
        subtitle={t('staff.subtitle')}
        actions={
          <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
            {t('staff.addWorker')}
          </Button>
        }
      />

      <Segmented
        label={t('staff.viewLabel')}
        options={[
          { value: 'workers', label: t('staff.viewWorkers') },
          { value: 'departments', label: t('staff.viewDepartments') },
        ]}
        value={view}
        onChange={(v) => setView(v as StaffView)}
      />

      {view === 'departments' ? (
        <DepartmentsView />
      ) : (
        <>
          <TextField
            label={t('common.search')}
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('staff.searchPlaceholder')}
            fullWidth
          />

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton lines={3} />
            </div>
          ) : isError ? (
            <EmptyState title={t('staff.errorLoading')} description={t('clients.tryRefreshing')} />
          ) : isEmpty ? (
            hasSearchQuery ? (
              <EmptyState
                icon={<WalletIcon className="w-12 h-12 text-on-surface-variant" />}
                title={t('staff.noWorkersFound')}
                description={t('staff.adjustSearch')}
              />
            ) : (
              <EmptyState
                icon={<WalletIcon className="w-12 h-12 text-on-surface-variant" />}
                title={t('staff.noWorkersYet')}
                description={t('staff.addFirstWorker')}
                action={<Button variant="tonal" onClick={handleAddNew}>{t('staff.addAWorker')}</Button>}
              />
            )
          ) : (
            <div className="bg-surface-container border border-outline-variant rounded-lg">
              <DataTable<WorkerListItem>
                columns={columns}
                rows={items}
                rowKey={(w) => w.id}
                onRowClick={(worker) => navigate(`/staff/${worker.id}`)}
                stackOnMobile
              />
            </div>
          )}
        </>
      )}

      <WorkerFormDialog open={formDialogOpen} worker={editingWorker} onClose={handleFormClose} />

      <ConfirmDialog
        open={!!deleteWorkerId}
        title={t('staff.removeWorkerTitle')}
        message={t('staff.removeWorkerMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteWorkerId) deleteMutation.mutate(deleteWorkerId)
        }}
        onCancel={() => setDeleteWorkerId(null)}
      />
    </div>
  )
}
